import { EventEmitter, Injectable } from '@angular/core';

import { AppState } from '../app-state';

import { RxReplicationState } from 'rxdb';
import { ScoutTask, ScoutPointPhoto, ScoutPoint } from '../models/scout-task';
import { RestClient } from './rest-client';
import { HttpClient, HttpEvent, HttpEventType } from '@angular/common/http';
import { BehaviorSubject, throwError, timer } from 'rxjs';
import { ScoutPointPhotoDoc, scoutPointPhotoMethods, scoutPointPhotoSchema } from '../schema/scout-point-photo-schema';
import { skipWhile, take, takeWhile } from 'rxjs/operators';
import { LookupService } from './lookup.service';
import { ScoutPointLocalPhotoService } from './scout-point-local-photo.service';
import { TestbedRxdbCouchCollectionService } from './testbed-rxdb/testbed-rxdb-couch-collection.service';
import { TestbedRxdbState } from './testbed-rxdb/testbed-rxdb-state';
import { AuthenticationService } from './authentication.service';
import { ReplicationStateListener } from './testbed-rxdb/testbed-rxdb-collection.service';


class ScoutPointPhotoClient extends RestClient<any> {

  constructor(http: HttpClient, appState: AppState) {
    super(http, 'scout', appState)
  }
}

// rxdb change events return 'any' objects so use these
// for type safety
export class RemotePhoto extends ScoutPointPhoto {
  _revisions: { ids:string[], start:number }[]
  _deleted:boolean
  _rev: string
}
export class PhotoChangeEvent {
  direction : "push" | "pull"
  change: {
    docs: RemotePhoto[]
    ok:boolean
    start_time:string,
    docs_read:number,
    docs_written:number,
    doc_write_failures:number,
    errors: any[]
    last_seq:string
  }
}
export class TransferPhotoProgress {
  bytesTransferred = 0
  bytesTotal = 0
  percentComplete = 0
  msg: string
  errorMsg : string
  bComplete : boolean = false
}
export class TransferPhotoProgressSubject extends BehaviorSubject<TransferPhotoProgress>{
    
  constructor( public photoId: string, transferProgress : TransferPhotoProgress) {
    super(transferProgress)    
  }
}

export class TransferPhotoCompleteEvent {
  constructor( public remotePhoto : RemotePhoto, public dataUrl:string, public error: any) {    
  }
}
@Injectable({ providedIn: 'root' })
export class ScoutPointPhotoService extends TestbedRxdbCouchCollectionService<ScoutPointPhoto, ScoutPointPhotoDoc> implements ReplicationStateListener {

  static readonly MAX_UPLOAD_RETRY_CNT = 5
  static readonly UPLOAD_RETRY_INTERVAL_MINUTES_LIST = [ 0.25, 1, 5, 30, 60 ]

  restClient: ScoutPointPhotoClient
  compatibleCameras : MediaDeviceInfo[] = []
  
  photosInTransaction : string[] = []

  downloadStartEmitter = new EventEmitter<TransferPhotoProgressSubject>()
  downloadProgressSubjects : TransferPhotoProgressSubject[] = []

  downloadCompleteEmitter = new EventEmitter<TransferPhotoCompleteEvent>()

  constructor( 
      appState : AppState, 
      authService: AuthenticationService, 
      rxdbState: TestbedRxdbState,
      http: HttpClient,
      protected lookupService : LookupService,
      public localPhotoService : ScoutPointLocalPhotoService ) {
    super('scout_point_photo', scoutPointPhotoSchema, appState, rxdbState, authService)
    this.methods = scoutPointPhotoMethods
    this.syncBatchSize = 500
    this.syncConfig = {
        push: true,
        pull: true,
        intervalMs: this.appState.settings.get('rxdb.collections.scout_point_photo.intervalMs',0),
        retry: true
    }
    this.restClient = new ScoutPointPhotoClient(http, appState)
  }

  syncChangeListeners : ReplicationStateListener[] = [] // scout point ref dialog needs to know if photo changed externally

  // TestBedRxdbMgr will call this one time for live syncs and on every non-live sync
  replicationStateCreated(replicationState: RxReplicationState) {
    replicationState.change$.subscribe( (changeEvent : PhotoChangeEvent) => {
      if ( changeEvent?.direction == 'pull' && changeEvent.change?.docs) {
        for( let doc of changeEvent.change.docs ) {
          if ( doc._deleted ) {
            this.processRemotePhotoDelete(doc)
          } else if ( !doc._revisions || doc._revisions.length == 0 ) {
            this.processRemotePhotoInsert(doc)
          } else {
            this.processRemotePhotoUpdate(doc)
          }
        }
      }
    })
    this.syncChangeListeners.forEach(listener=>listener.replicationStateCreated(replicationState))
  }


  replicationStateCanceled(replicationState: RxReplicationState) {
    this.syncChangeListeners.forEach(listener=>listener.replicationStateCanceled(replicationState))
  }


  assignWhoAndWhen(destPhoto:ScoutPointPhoto){
    destPhoto.modifiedDate = this.nowTimeStamp()
    destPhoto.userId = this.appState.getUser()._id
  }


  beginPhotoTransaction( photoId : string ) {
    return new Promise<void>( (completeFn,errorFn) => {
      timer(0,1000).pipe( takeWhile( (_,index) => index < 60 && this.photosInTransaction.includes(photoId) ) ).subscribe( 
        _ => { console.warn('Photo transaction locked for photo id: ' + photoId)}, 
        error => errorFn(error), 
        () => {
          this.photosInTransaction.push(photoId)
          completeFn()
        }
      )
    })
  }


  async dataUrlToFileObject( photoUrl : string, fileName : string, mimeType : string ){
    const urltoFile = (url, filename, mimeType) => {
      return (fetch(url)
          .then(function(res){return res.arrayBuffer();})
          .then(function(buf){return new File([buf], filename,{type:mimeType});})
      );
    }
    const photoFile : File = await urltoFile(photoUrl, fileName, mimeType)
    return photoFile
  }


  deleteRemotePhotoFile( photo : ScoutPointPhoto ) {
    return this.deleteRemotePhotoFileById( photo._id, photo)
  }


  deleteRemotePhotoFileById(fileId: string, metaData: Object) {
    if (!fileId) {
      const errMsg = 'Delete scout photo file request requires fileId.'
      console.error(errMsg)
      return throwError(errMsg)
    }
    const strDeleteUrl = `${this.restClient.urlPath}/ref-file/${fileId}`
    console.log('deleteRemotePhotoFileById: ' + strDeleteUrl)
    return this.restClient.http.request('DELETE', strDeleteUrl, { body: metaData })
  }


  deleteScoutPointPhotos = async ( scoutPoint: ScoutPoint ) => {
    console.log('deleteScoutPointPhotos('+scoutPoint.name+')')
    try {
      const photoDocs = await this.rxCollection.find().where('scoutPointId').eq(scoutPoint.id).exec()
      console.log('Found ' + photoDocs.length + ' photos to delete for scout point ' + scoutPoint.name)
      for( const photoDoc of photoDocs ) {
        console.log('Removing photo "' + photoDoc.fileName + '"')
        try {
          await this.removeLocalPhotoFile(photoDoc._id)
          if ( !this.appState.isOffline() ) {
            await this.deleteRemotePhotoFile(photoDoc)
          }
        } catch ( error ) {
          console.error('Failed removing remote file for scout photo ' + photoDoc.fileName)
          console.error(error.message)
        }        
        await photoDoc.remove()
      }
      this.publishInfo( 'Removed ' + photoDocs.length + ' photo(s) for scout point "' + scoutPoint.name + '"', this, 60)
    } catch ( error ) {
      console.log('Failed removing photo documents associated with scout point "' + scoutPoint?.name + '"')
      throw error
    }
  }
  

  async downloadScoutRefPhotoFile(photoId: string, dataType: string, progressSetter : (loaded:number,total:number)=>void = (l,t)=>{}) : Promise<string> {
    if (!photoId) {
      const errMsg = 'Download scout photo request requires photoId.'
      console.error(errMsg)
      throw new Error(errMsg)
    }
    const strDownloadUrl = `${this.restClient.urlPath}/ref-file/${photoId}`
    console.log('downloadScoutRefPhotoFile(): ' + strDownloadUrl)
    
    let downloadProgress = new TransferPhotoProgress()
    let downloadProgressSubject = new TransferPhotoProgressSubject(photoId,downloadProgress)
    this.downloadProgressSubjects.push(downloadProgressSubject)
    this.downloadStartEmitter.next(downloadProgressSubject)

    return new Promise( (resolve, reject) => {
    let total = 0
    
    const compositeProgressSetter = (loaded:number) => {
      progressSetter(loaded,total)
      downloadProgress.bytesTransferred = loaded
      downloadProgress.bytesTotal = total
      downloadProgress.msg = (downloadProgress.bytesTransferred/(1024*1024.0)).toFixed(2) + ' MB downloaded...'
      if ( downloadProgress.bytesTotal ) {
        downloadProgress.percentComplete = Math.round(downloadProgress.bytesTransferred / downloadProgress.bytesTotal * 100)
      } else {
        downloadProgress.percentComplete = 0
      }
      downloadProgressSubject.next(downloadProgress)      
    }

    this.restClient.http.get<ArrayBuffer>(strDownloadUrl, { 
      responseType: 'blob' as 'json',
      reportProgress: true,
      observe: 'events'
    }).subscribe( async (event: HttpEvent<any>) => {
      switch (event.type) {
        case HttpEventType.ResponseHeader:
          if ( event.status != 200 ) {
            const msg = 'Image load failed: ' + event.statusText
            console.error('downloadScoutRefPhotoFile() http response: ' + msg)
            compositeProgressSetter(0)
            reject(new Error(msg))
          }
          break;
        case HttpEventType.DownloadProgress:
          total = event.total
          //console.log( 'downloaded: ' +  event.loaded + ', total: ' + (event.total!==undefined?total:'unknown size'))
          compositeProgressSetter(event.loaded);
          break;
        case HttpEventType.Response:
          if ( event.status == 200 ) {
            let binaryData = [];
            binaryData.push(event.body);
            const blob = new Blob(binaryData, {type: dataType})
            var reader = new FileReader()
            const fileLoadPromise = new Promise<string>( (completeFn,errorFn) => {
              reader.onload = async (event) => {	
                console.log('Loaded ' + dataType + ' photo file from server (id=' + photoId + ')')	
                const resultDataUrl = reader.result as string
                completeFn(resultDataUrl)
              }
              reader.onerror = (error)  => {
                console.log('error in blob read')
                errorFn(error)
              }
            })
            try {
              reader.readAsDataURL(blob)
              const strDataUrl = await fileLoadPromise
              downloadProgress.bComplete = true
              compositeProgressSetter(Math.max(downloadProgress.bytesTransferred,downloadProgress.bytesTotal))
              resolve(strDataUrl)
            } catch ( error ) {
              downloadProgress.bComplete = true
              compositeProgressSetter(0)
              reject(error)
            }
          }
          downloadProgressSubject.complete()
          this.downloadProgressSubjects = this.downloadProgressSubjects.filter(subject=>subject.photoId!=photoId)                
        }
    },
    error => {
      console.error('downloadScoutRefPhotoFile() error: ' + error?.message)
      this.downloadProgressSubjects = this.downloadProgressSubjects.filter(subject=>subject.photoId!=photoId) 
      reject(error)
    })})   
  }


  endPhotoTransaction( photoId : string ) {
    this.photosInTransaction = this.photosInTransaction.filter( id=>id!=photoId)
  }


  getDefaultCamera() {
    return this.compatibleCameras[this.compatibleCameras.length-1]
  }


  initPhotosForPointType( photoDocs : ScoutPointPhotoDoc[], pointType: string, preparedPhotoDocs : ScoutPointPhotoDoc[] = null ){
    console.log('initPhotosForPointType()')

    if ( preparedPhotoDocs == null ) {
      preparedPhotoDocs = photoDocs
    }
    
    const measurementViewTitles = [
      "Close up of wall ref 1 measurement at point",
      "Close up of wall ref 2 measurement at point",
      "Medium-angle of point showing wall ref 1",
      "Medium-angle of point showing wall ref 2"
    ]

    const wideAngleViewTitles = [
      "Wide-angle of point with wall refs 1 & 2 in view",
      "Wide-angle of point from opposite direction",
      "Wide-angle of point from another direction",
      "Wide-angle toward nearest window (optional)"
    ]
        
    const preparePhotos = ( titles : string[], groupName : string) => {
      let preparedPhotos : ScoutPointPhotoDoc[] = []
      titles.forEach( (requiredTitle, index) => {
        let photo = photoDocs.find(photo=>photo.group==groupName&&photo.title==requiredTitle)
        if ( !photo ) {
          photo = this.rxCollection.newDocument()
          photo.pointType = pointType
          photo.title = requiredTitle
          photo.group = groupName
          photo.groupOrderIndex = index
        } else if ( photo.groupOrderIndex != index ) {
          console.warn('Existing required photo did not have expected group index')
          photo.atomicPatch({groupOrderIndex:index})
        }
        preparedPhotos.push(photo)
      })
      return preparedPhotos
    }

    if ( preparedPhotoDocs != photoDocs ) {
      const unsavedNewDocs = preparedPhotoDocs.filter(doc=>!doc._id)
      photoDocs.push( ...unsavedNewDocs )
    }
    const measurementDocs = preparePhotos( measurementViewTitles, 'measurement' )
    const wideAngleDocs = preparePhotos( wideAngleViewTitles, 'wide-angle' )
    const pathViewDocs = photoDocs.filter(doc=>doc.group=='point-to-point')
    const additionalDocs = photoDocs.filter(doc=>doc.group=='additional')
    const unknownDocs = photoDocs.filter(doc=>!['measurement','wide-angle','point-to-point','additional'].includes(doc.group))
    const sortGroup = ( lhs,rhs ) => (lhs.groupOrderIndex > rhs.groupOrderIndex) ? 1 : -1;
    const docGroups = [ measurementDocs, wideAngleDocs, pathViewDocs, additionalDocs, unknownDocs ]
    preparedPhotoDocs.length = 0
    docGroups.forEach( docs => {
      docs.sort(sortGroup)
      preparedPhotoDocs.push(...docs)
    })
    //console.log('sorted rxdb docs: ' + this.photoDocs.map(doc=>'[group:'+doc.group +',title:' +doc.title+']' ).join(','))
  }


  updatingCompatibleCameras = false
  async updateCompatibleCameras() {
    if ( this.updatingCompatibleCameras ) {
      await timer(0,1000).pipe( skipWhile(_=> this.updatingCompatibleCameras), take(1) ).toPromise()
      return this.compatibleCameras
    }  
    this.updatingCompatibleCameras = true
    let mediaDevices : MediaDeviceInfo[]
    try {
      this.publishInfo('Probing for compatible cameras...',this,15)
      mediaDevices = (await navigator.mediaDevices.enumerateDevices()).filter(d=>d.kind=='videoinput')
      //console.log('Avail cams: ' + mediaDevices.map(d=> `${d.label}(id=${d.deviceId})`))
      mediaDevices.forEach(d=> (d as any).widths = [])
      
      let widths = [ 1920, 1280, 1024 ]

      const scoutConfig = await this.lookupService.rxCollection.findOne('scout').exec()
      if ( scoutConfig ) {
        const photoConfig = scoutConfig.values.find(v=>v.id=='photo')
        if ( photoConfig?.allowedCameraWidths ) {
          widths = photoConfig?.allowedCameraWidths
        }
      }

      for( const mediaDevice of mediaDevices ) {
        for( const width of widths ) {
          var constraints : MediaStreamConstraints = {
            video: {
              deviceId: mediaDevice.deviceId,
              width: width
            }
          }
          try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
            const tracks = mediaStream.getTracks()
            for (const track of tracks ) {
              const mediaDevice:MediaDeviceInfo = mediaDevices.find(device=>device.deviceId == track.getSettings().deviceId);
              (mediaDevice as any).widths.push(width)
              track.stop()
            }           
          } catch ( error ) {
            console.warn('No video devices support width ' + width + ' for ' + mediaDevice.label)
          }
        }
      }
      mediaDevices.forEach(d=> {
        (d as any).widths.sort(function(a, b){return a-b}) 
      })
      
      this.compatibleCameras = mediaDevices.filter(d=>(d as any).widths.length)
      console.log(JSON.stringify(this.compatibleCameras))  
      if ( this.compatibleCameras.length ) {
        this.publishInfo(null,this)
        this.publishSuccess('Found ' + this.compatibleCameras.length + ' camera(s)',this,5)
      } else {
        this.publishInfo('No compatible cameras found (allowed widths:' + widths + ').',this,30)
      }
    } catch ( error ) {
      console.error(error)
    }
    this.updatingCompatibleCameras = false
    return this.compatibleCameras
  }


  getPathAndPoint(scoutTask: ScoutTask, pathId: string, pointId: string) {
    const scoutPath = scoutTask.scoutPaths.find(sp => sp.id == pathId)
    const scoutPoint = scoutPath.scoutPoints.find(sp => sp.id == pointId)
    return {
      scoutPath: scoutPath,
      scoutPoint: scoutPoint
    }
  }


  async getPathPointAndPhoto(scoutTask: ScoutTask, pathId: string, pointId: string, photoId: string) {
    const { scoutPath, scoutPoint } = this.getPathAndPoint(scoutTask, pathId, pointId)
    const photoDoc = await this.rxCollection.findOne(photoId).exec()
    return {
      scoutPath: scoutPath,
      scoutPoint: scoutPoint,
      scoutPhotoDoc: photoDoc
    }
  }


  inPhotoTransaction( photoId : string ) {
    return this.photosInTransaction.some(id=>id==photoId) 
  }


  async isLocalPhotoFileCurrent( photo : ScoutPointPhoto ) : Promise<boolean> {
    return this.localPhotoService.isLocalPhotoFileCurrent(photo)
  }  
  async loadLocalPhotoFile( id: string, checksum: string ) {
    return this.localPhotoService.loadLocalPhotoFile(id,checksum)
  }
  async removeLocalPhotoFile( id: string ) {
    return this.localPhotoService.removeLocalPhotoFile(id)
  }
  async saveLocalPhotoFile( photo : ScoutPointPhoto, photoUrl : string ) {
    return this.localPhotoService.saveLocalPhotoFile(photo,photoUrl)
  }

  processRemotePhotoDelete = async (remotePhoto : RemotePhoto) => {
    console.log('processRemotePhotoDelete(): id=' + remotePhoto?._id)
    await this.beginPhotoTransaction(remotePhoto._id)
    try {
      this.removeLocalPhotoFile(remotePhoto._id)
    } catch ( error ) {
      console.error('processRemotePhotoDelete() removing localPhotoUrlDoc failed: ' + remotePhoto._id )
    }
    this.endPhotoTransaction(remotePhoto._id)
  }

  
  processRemotePhotoInsert = async (remotePhoto : RemotePhoto) => {
    const isNew = true
    await this.processRemotePhotoChange(remotePhoto, isNew)
  }


  processRemotePhotoUpdate = async (remotePhoto : RemotePhoto) => {
    const isNew = false
    await this.processRemotePhotoChange(remotePhoto, isNew)
  }

  maxPhotoCacheAgeDays = 10.0
  processRemotePhotoChange = async ( remotePhoto : RemotePhoto, bIsNewPhoto : boolean ) => {
    //console.log('processRemotePhotoChange(): fileName=' + remotePhoto?.fileName + ', uploadDate=' + remotePhoto.uploadDate + ', bIsNewDoc=' + bIsNewPhoto + ', id=' + remotePhoto?._id)
    //console.log(JSON.stringify(remotePhoto))
    const bPhotoHasFile = remotePhoto.uploadDate && remotePhoto.fileName
    const bAppUserOwnsPhoto = remotePhoto?.userId && remotePhoto.userId == this.appState.getUser()?._id
      
    if ( bPhotoHasFile && bAppUserOwnsPhoto ) {
      const uploadDateMs = Date.parse(remotePhoto.uploadDate)
      if ( isNaN(uploadDateMs) ) {
        console.warn('Remote photo change detected but upload date is not valid: ' + remotePhoto.uploadDate)
        return
      }
      
      const elapsedDays = (new Date().getTime() - uploadDateMs) / (1000.0*60*60*24)
      if ( elapsedDays > this.maxPhotoCacheAgeDays ) {
        //console.warn('photo not recent. elapsedDays: ' + elapsedDays + ', time stamp: ' + remotePhoto.uploadDate)
        return
      }
      await this.beginPhotoTransaction(remotePhoto._id)
      try {
        const cachedFileDoc = await this.localPhotoService.getById(remotePhoto._id)
        const bHaveLocalCopy = cachedFileDoc != null
        const bLocalCopyStale = bHaveLocalCopy && (remotePhoto.fileName != cachedFileDoc.name || remotePhoto.fileChecksum != cachedFileDoc.checksum)
        if ( !bHaveLocalCopy || bLocalCopyStale ) {
          console.log('processRemotePhotoChange() caching ' + remotePhoto.fileName + ', bHaveLocalCopy: ' + bHaveLocalCopy + ', bLocalCopyStale: ' + bLocalCopyStale)
          try {
            const photoUrl = await this.downloadScoutRefPhotoFile(remotePhoto._id,remotePhoto.mimeType)
            await this.saveLocalPhotoFile(remotePhoto, photoUrl)
            this.downloadCompleteEmitter.next(new TransferPhotoCompleteEvent(remotePhoto,photoUrl,null))
          } catch ( error ) {
            this.downloadCompleteEmitter.next(new TransferPhotoCompleteEvent(remotePhoto,null,error))
            throw error
          }
        }
      } catch (error) {
        console.error('processRemotePhotoChange() failed caching photo ' + remotePhoto.fileName)
        console.error(error?.message)
      }
      this.endPhotoTransaction(remotePhoto._id)
      return
    } 
    if ( !bPhotoHasFile && !bIsNewPhoto ) { 
      // don't remove if new photo because user may have cleared main rxdb 
      // with photo but saved/cached photo stored in local cache db (new photo didn't originate from remote source)
      try { 
        this.removeLocalPhotoFile(remotePhoto._id)
      } catch ( error ) {
        console.error('processRemotePhotoChange() failed to remove cached photo after detecting remote photo has no image. id=' + remotePhoto._id)
      }
    }
  }
}
  